import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { extractNestedAnswers } from "@/encounters/components/PostpartumEncounter/utils/postpartum";
import {
  PostpartumEncounterQuestionnaireURLs,
  PostpartumFeedingQuestionsLinkIds,
} from "@/encounters/constants/postpartum-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";

const FeedingDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          PostpartumEncounterQuestionnaireURLs.Feeding,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no feeding data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Feeding</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No feeding data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If feeding data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Extract values for all feeding fields using extractNestedAnswers
  const previousAnswers = extractNestedAnswers(effectivePreviousVersion);
  const currentAnswers = extractNestedAnswers(currentVersion);

  const previousFeedingType =
    previousAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingType]?.join(", ") || "";
  const currentFeedingType =
    currentAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingType]?.join(", ") || "";

  const previousFeedingSupplement =
    previousAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingSupplement]?.join(", ") ||
    "";
  const currentFeedingSupplement =
    currentAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingSupplement]?.join(", ") || "";

  const previousFeedingWith =
    previousAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingWith]?.join(", ") || "";
  const currentFeedingWith =
    currentAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingWith]?.join(", ") || "";

  const previousFeedingSupply =
    previousAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingSupply]?.join(", ") || "";
  const currentFeedingSupply =
    currentAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingSupply]?.join(", ") || "";

  const previousFeedingPain =
    previousAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingPain]?.join(", ") || "";
  const currentFeedingPain =
    currentAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingPain]?.join(", ") || "";

  const previousFeedingRedness =
    previousAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingRedness]?.join(", ") || "";
  const currentFeedingRedness =
    currentAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingRedness]?.join(", ") || "";

  const previousFeedingDucts =
    previousAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingDucts]?.join(", ") || "";
  const currentFeedingDucts =
    currentAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingDucts]?.join(", ") || "";

  const previousNotes =
    previousAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingNotes]?.join(", ") || "";
  const currentNotes =
    currentAnswers[PostpartumFeedingQuestionsLinkIds.PostpartumFeedingNotes]?.join(", ") || "";

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Feeding</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Type</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentFeedingType}
                  previous={previousFeedingType}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Supplement</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentFeedingSupplement}
                  previous={previousFeedingSupplement}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding With</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentFeedingWith}
                  previous={previousFeedingWith}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Supply</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentFeedingSupply}
                  previous={previousFeedingSupply}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Pain</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentFeedingPain}
                  previous={previousFeedingPain}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Redness</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentFeedingRedness}
                  previous={previousFeedingRedness}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Ducts</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentFeedingDucts}
                  previous={previousFeedingDucts}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Type</TextWrapper>
                <WordsDiff current={currentFeedingType} previous={previousFeedingType} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Supplement</TextWrapper>
                <WordsDiff
                  current={currentFeedingSupplement}
                  previous={previousFeedingSupplement}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding With</TextWrapper>
                <WordsDiff current={currentFeedingWith} previous={previousFeedingWith} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Supply</TextWrapper>
                <WordsDiff current={currentFeedingSupply} previous={previousFeedingSupply} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Pain</TextWrapper>
                <WordsDiff current={currentFeedingPain} previous={previousFeedingPain} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Redness</TextWrapper>
                <WordsDiff current={currentFeedingRedness} previous={previousFeedingRedness} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Feeding Ducts</TextWrapper>
                <WordsDiff current={currentFeedingDucts} previous={previousFeedingDucts} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default FeedingDiff;
